import { useRef, useEffect, useState } from 'react';
import { isIframeEmpty } from '../utils/iframeHelper';

interface UsePdfViewerOptions {
    pdfPath: string;
    getViewerUrl: (pdfPath: string) => string;
    maxRetries?: number;
    checkInterval?: number;
}

interface UsePdfViewerReturn {
    iframeRef: React.RefObject<HTMLIFrameElement>;
    containerRef: React.RefObject<HTMLDivElement>;
    viewerUrl: string;
    retryKey: number;
    isLoading: boolean;
}

export const usePdfViewer = ({
    pdfPath,
    getViewerUrl,
    maxRetries = 5,
    checkInterval = 2000,
}: UsePdfViewerOptions): UsePdfViewerReturn => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [retryKey, setRetryKey] = useState(0);
    const [viewerUrl, setViewerUrl] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const periodicCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const loadStartTimeRef = useRef<number>(0);
    const isContentLoadedRef = useRef(false);
    const isRetryingRef = useRef(false);
    const currentRetryKeyRef = useRef(0);
    const iframeClassNameRef = useRef<string>('');
    const iframeTitleRef = useRef<string>('');

    useEffect(() => {
        currentRetryKeyRef.current = retryKey;
    }, [retryKey]);

    const previousViewerUrlRef = useRef<string>('');

    useEffect(() => {
        const url = getViewerUrl(pdfPath);

        previousViewerUrlRef.current = url;
        setViewerUrl(url);
        setIsLoading(true);
        
        const destroyAndRecreate = () => {
            const container = containerRef.current;
            if (!container) {
                loadStartTimeRef.current = Date.now();
                isContentLoadedRef.current = false;
                isRetryingRef.current = false;
                return;
            }

            const existingIframe = iframeRef.current;
            
            if (existingIframe) {
                if (!iframeClassNameRef.current) {
                    iframeClassNameRef.current = existingIframe.className;
                }
                if (!iframeTitleRef.current) {
                    iframeTitleRef.current = existingIframe.title;
                }
            }

            const className = iframeClassNameRef.current || container.className.replace('-container', '-iframe') || 'iframe';
            const title = iframeTitleRef.current || 'PDF Viewer';
            const style = existingIframe?.getAttribute('style');

            if (existingIframe && container.contains(existingIframe)) {
                try {
                    container.removeChild(existingIframe);
                } catch (error) {
                }
            }
            const newIframe = document.createElement('iframe');
            newIframe.src = url;
            newIframe.className = className;
            newIframe.title = title;
            if (style) {
                newIframe.setAttribute('style', style);
            }

            container.appendChild(newIframe);
            (iframeRef as any).current = newIframe;
            
            if (!iframeClassNameRef.current) {
                iframeClassNameRef.current = className;
            }
            if (!iframeTitleRef.current) {
                iframeTitleRef.current = title;
            }
            
            loadStartTimeRef.current = Date.now();
            isContentLoadedRef.current = false;
            isRetryingRef.current = false;
            
            setTimeout(() => {
                try {
                    const isEmpty = isIframeEmpty(newIframe);
                    if (!isEmpty) {
                        isContentLoadedRef.current = true;
                        setIsLoading(false);
                    } else {
                        setIsLoading(true);
                    }
                } catch (error) {
                    setIsLoading(true);
                }
            }, 1000);
        };

        setTimeout(() => {
            destroyAndRecreate();
        }, 0);
    }, [retryKey, pdfPath, getViewerUrl]);

    useEffect(() => {
        if (!viewerUrl) return;

        const maxCheckWindow = 10000;
        const startDelay = 2000;

        if (periodicCheckIntervalRef.current) {
            clearInterval(periodicCheckIntervalRef.current);
            periodicCheckIntervalRef.current = null;
        }

        const startCheckTimeout = setTimeout(() => {
            periodicCheckIntervalRef.current = setInterval(() => {
            const timeSinceLoad = Date.now() - loadStartTimeRef.current;

            if (timeSinceLoad > maxCheckWindow) {
                if (periodicCheckIntervalRef.current) {
                    clearInterval(periodicCheckIntervalRef.current);
                    periodicCheckIntervalRef.current = null;
                }
                if (!isContentLoadedRef.current) {
                    setIsLoading(false);
                }
                return;
            }

            if (isContentLoadedRef.current || isRetryingRef.current) {
                return;
            }

            try {
                const iframe = iframeRef.current;
                if (!iframe) return;

                const isEmpty = isIframeEmpty(iframe);

                if (isEmpty) {
                    if (isRetryingRef.current) {
                        return;
                    }

                    if (currentRetryKeyRef.current < maxRetries) {
                        const newRetryKey = currentRetryKeyRef.current + 1;
                        isRetryingRef.current = true;
                        
                        if (periodicCheckIntervalRef.current) {
                            clearInterval(periodicCheckIntervalRef.current);
                            periodicCheckIntervalRef.current = null;
                        }
                        
                        isContentLoadedRef.current = false;
                        
                        setRetryKey((prev) => {
                            const newKey = prev + 1;
                            currentRetryKeyRef.current = newKey;
                            return newKey;
                        });
                    } else {
                        setIsLoading(false);
                    }
                } else {
                    if (!isContentLoadedRef.current) {
                    }
                    isContentLoadedRef.current = true;
                    setIsLoading(false);
                }
            } catch (error) {
            }
            }, checkInterval);
        }, startDelay);

        const stopChecksTimeout = setTimeout(() => {
            if (periodicCheckIntervalRef.current) {
                clearInterval(periodicCheckIntervalRef.current);
                periodicCheckIntervalRef.current = null;
            }
        }, maxCheckWindow + startDelay);

        return () => {
            clearTimeout(startCheckTimeout);
            clearTimeout(stopChecksTimeout);
            if (periodicCheckIntervalRef.current) {
                clearInterval(periodicCheckIntervalRef.current);
                periodicCheckIntervalRef.current = null;
            }
        };
    }, [viewerUrl, checkInterval, maxRetries]);

    return {
        iframeRef,
        containerRef,
        viewerUrl,
        retryKey,
        isLoading,
    };
};

